import { t } from '../i18n/i18n';
/**
 * The premium emote picker (`design/gdd/cosmetics-store.md`).
 *
 * Opened with **B** — the key freed when `05d9e7e` removed bandages and left
 * it inert ("the old B key is inert rather than throwing"). Reusing it costs no
 * muscle memory and takes no key a gameplay action needs: 1-5 are abilities,
 * 6-7 the satchel, and J/K/L/F/R/Q/E/T are all combat or interaction.
 *
 * Built like every other panel in `engine/ui/`: plain DOM plus Tailwind
 * utilities, `pointerdown` rather than `mousedown` so a tap works, and every
 * button a real 44px target per CLAUDE.md. It has to be usable on a phone,
 * where there is no B key at all — which is why it is a tappable list and not
 * a keyboard-only radial.
 *
 * Shows only what the player owns. An empty list says so and points at the
 * store rather than rendering four locked rows: a dungeon is the wrong place to
 * sell something, and a row you cannot press is just noise on a small screen.
 */

export interface EmoteOption {
  id: string;
  label: string;
}

export class EmoteMenu {
  private readonly root: HTMLDivElement;
  private options: EmoteOption[] = [];
  private open = false;

  /** Set by the caller; fires with the chosen emote's cosmetic def id. */
  onPick: (emoteId: string) => void = () => {};

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'fixed left-1/2 -translate-x-1/2 bottom-28 z-[1001] hidden '
      + 'flex-col gap-1.5 p-2 frame-raised pointer-events-auto '
      + 'max-w-[90vw] w-56';
    container.appendChild(this.root);
  }

  setOptions(options: readonly EmoteOption[]): void {
    this.options = [...options];
    this.render();
  }

  toggle(): void {
    this.open = !this.open;
    this.root.classList.toggle('hidden', !this.open);
    this.root.classList.toggle('flex', this.open);
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.root.classList.add('hidden');
    this.root.classList.remove('flex');
  }

  private render(): void {
    this.root.replaceChildren();

    if (this.options.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'text-[12px] text-text-dim leading-snug px-1 py-0.5';
      empty.textContent = t('No emotes yet — they are Ember cosmetics, bought at the Sconce.');
      this.root.appendChild(empty);
      return;
    }

    for (const option of this.options) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn-stone justify-start w-full';
      button.textContent = option.label;
      button.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        // Closed on pick: an emote has a cooldown, so leaving the panel open
        // would mostly show buttons that do nothing.
        this.close();
        this.onPick(option.id);
      });
      this.root.appendChild(button);
    }
  }

  dispose(): void {
    this.root.remove();
  }
}
